import type { IGroupChatMsg, IGroupChatUser } from "@common/types/api/r2-group-chat-types";
import { GroupChatBase } from "@core/base/group-chat-base";
import type { ISessionInfo } from "@core/base/session-base.types";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { OutlookHost } from "@core/shared/office-hosts/outlook-host";
import { encodeOutlookGroupId } from "@core/shared/outlook-conversation-id";
import type { IGuid } from "@core/types/chat.types";
import type {
    IGroupChatAuthor,
    IGroupChatSendResult,
    IGroupChatUiMessage,
} from "@core/types/group-chat-message.types";
import { environment } from "@env";
import { logger } from "@services/logger/logger.service";
import { SessionCryptoService } from "@services/session-crypto/session-crypto.service";
import { BehaviorSubject, Observable, Subscription, combineLatest, distinctUntilChanged, map } from "rxjs";
import { v4 as uuidV4 } from 'uuid';
import { OfficeGroupChatV1 } from "./office-group-chat-v1";

const GROUP_POLL_INTERVALS_MS = [ 5_000, 10_000, 20_000 ];
const AI_POLL_INTERVALS_MS = [ 10_000, 20_000, 40_000, 60_000 ];
const POLLS_PER_INTERVAL = 5;
const MAIL_CONSENT_RETRY_INTERVAL_MS = 2 * 60_000;

export class OfficeGroupChatAdapterServiceV1 extends OfficeGroupChatV1 implements GroupChatBase
{
    private isMessagesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private isIdentityUnavailable = new BehaviorSubject<boolean>(false);
    private needsMailConsent = new BehaviorSubject<boolean>(false);
    private documentId = new BehaviorSubject<string | null>(null);
    private consentRequest: Promise<void> | null = null;
    private consentRequestInteractive = false;
    private consentRetryTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly isAvailable: BehaviorSubject<boolean>;
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollStep = 0;
    private pollAttempts = 0;
    private isPollingEnabled = false;
    private isPolling = false;
    private pendingPollAfterCurrent: boolean | null = null;
    private pollGeneration = 0;
    // Messages from other participants newer than the last-read marker; surfaced
    // as the group-tab notification badge. Recomputed on every load/poll and
    // zeroed once the user is focused and scrolled to the bottom.
    private unreadCount = new BehaviorSubject<number>(0);
    private firstUnreadId = new BehaviorSubject<number | null>(null);
    // The add-in window holds focus and is visible; reported by the conversation
    // shell so it is known on both conversation tabs.
    private isWindowActive = false;
    // The group chat view — not the AI chat — is the one on screen.
    private isViewActive = false;
    // True while the message list is scrolled to the bottom of the unread run.
    // The last-read marker only advances while both are true, so messages that
    // arrive unfocused stay unread (separator visible) until the user scrolls
    // down to them.
    private isAtBottom = false;
    private initialization: Promise<void> | null = null;
    private readonly sessionSubscription = new Subscription();
    private isAuthenticated = true;
    private documentResolved = false;
    private isDestroyed = false;
    private readonly documentChangeHandler = (): Promise<void> =>
    {
        this.resetDocument();
        return this.hasInitialized ? this.init() : Promise.resolve();
    };
    // Only reload on session changes after the authenticated shell initialized the chat.
    private hasInitialized = false;

    constructor(
        office: OfficeService,
        session: OfficeSessionService,
        sessionCrypto: SessionCryptoService,
    )
    {
        super(office, session, sessionCrypto);
        this.isAvailable = new BehaviorSubject<boolean>(!(office.getHost() instanceof OutlookHost));
        this.defineHandlerForDocumentChange();
        this.defineHandlerForSessionChange();
    }

    public $isMessagesLoading(): Observable<boolean>
    {
        return this.isMessagesLoading.asObservable();
    }

    public $isIdentityUnavailable(): Observable<boolean>
    {
        return this.isIdentityUnavailable.asObservable();
    }

    public $needsMailConsent(): Observable<boolean>
    {
        return this.needsMailConsent.asObservable();
    }

    public $documentId(): Observable<string | null>
    {
        return this.documentId.asObservable();
    }

    public requestMailConsent(): Promise<void>
    {
        return this.refreshMailConsent(true);
    }

    private refreshMailConsent(interactiveConsent: boolean): Promise<void>
    {
        if (!environment.outlook_group_graph_enabled)
        {
            return Promise.resolve();
        }
        if (this.consentRequest)
        {
            if (!interactiveConsent || this.consentRequestInteractive)
            {
                return this.consentRequest;
            }
            const generation = this.documentGeneration;
            const retryInteractively = (): Promise<void> =>
            {
                if (generation === this.documentGeneration && this.needsMailConsent.getValue() && this.isOutlookGroupActive())
                {
                    return this.refreshMailConsent(true);
                }
                return Promise.resolve();
            };
            return this.consentRequest.then(retryInteractively, retryInteractively);
        }
        if (!(this.officeService.getHost() instanceof OutlookHost) || !this.isOutlookGroupActive())
        {
            return Promise.resolve();
        }
        this.clearConsentRetry();
        const request = this.requestMailConsentInternal(interactiveConsent);
        this.consentRequest = request;
        this.consentRequestInteractive = interactiveConsent;
        const clearRequest = (): void =>
        {
            if (this.consentRequest === request)
            {
                this.consentRequest = null;
                this.consentRequestInteractive = false;
                this.scheduleConsentRetry();
            }
        };
        void request.then(clearRequest, clearRequest);
        return request;
    }

    private scheduleConsentRetry(): void
    {
        if (!environment.outlook_group_graph_enabled || this.consentRetryTimer !== null || this.consentRequest || !this.isWindowActive
            || !this.needsMailConsent.getValue() || !(this.officeService.getHost() instanceof OutlookHost)
            || !this.isOutlookGroupActive())
        {
            return;
        }
        this.consentRetryTimer = setTimeout(() =>
        {
            this.consentRetryTimer = null;
            void this.refreshMailConsent(false).catch(() => logger.error('groupChat: mail consent refresh failed'));
        }, MAIL_CONSENT_RETRY_INTERVAL_MS);
    }

    private clearConsentRetry(): void
    {
        if (this.consentRetryTimer !== null)
        {
            clearTimeout(this.consentRetryTimer);
            this.consentRetryTimer = null;
        }
    }

    private async requestMailConsentInternal(interactiveConsent: boolean): Promise<void>
    {
        const host = this.officeService.getHost();
        if (!(host instanceof OutlookHost))
        {
            return;
        }
        const generation = this.documentGeneration;
        const snapshot = host.captureSelection();
        const mailboxHint = Office.context.mailbox?.userProfile?.emailAddress ?? '';
        const isActive = (): boolean => generation === this.documentGeneration
            && this.isOutlookGroupActive() && host.isSelectionCurrent(snapshot);
        const anchorId = await this.resolveOutlookRoot(host, snapshot, mailboxHint, isActive, interactiveConsent);
        if (!isActive())
        {
            return;
        }
        this.needsMailConsent.next(this.mailConsentRequired);
        const id = anchorId ? await encodeOutlookGroupId(anchorId) : null;
        if (!isActive())
        {
            return;
        }
        if (!id || this.document?.id === id)
        {
            return;
        }
        this.resetDocument();
        this.document = { vendor_id: 'microsoft', type: 'mail', id };
        this.documentResolved = true;
        await this.init();
    }

    protected override isOutlookGroupActive(): boolean
    {
        return this.isAuthenticated && this.hasInitialized && !this.isDestroyed;
    }

    public $isAvailable(): Observable<boolean>
    {
        return this.isAvailable.asObservable().pipe(distinctUntilChanged());
    }

    public getMessages(): Observable<IGroupChatUiMessage[]>
    {
        return combineLatest([ this.$messages, this.$users ]).pipe(
            map(([ msgs, users ]) => this.toUiMessages(msgs, users)),
        );
    }

    public getMessagesValue(): IGroupChatUiMessage[]
    {
        return this.toUiMessages(this.$messages.getValue(), this.$users.getValue());
    }

    public getUsers(): Observable<IGroupChatUser[]>
    {
        return this.$users.pipe(map(users => Array.from(users.values())));
    }

    public async sendMessage(text: string, parentId?: number | null): Promise<IGroupChatSendResult>
    {
        const generation = this.documentGeneration;
        const timestamp = Date.now();
        const traceID = uuidV4() as IGuid;
        const trimmed = text.trim();
        if (!trimmed)
        {
            return this.errorHandler('Empty message', traceID, timestamp);
        }

        try
        {
            await this.addMessage(trimmed, parentId, traceID);
            if (generation === this.documentGeneration)
            {
                this.syncReadState();
            }
            return { isError: false, text: '', traceID, timestamp };
        }
        catch (e)
        {
            logger.error('groupChat: send failed', e);
            return this.errorHandler(e, traceID, timestamp);
        }
    }

    public async deleteMessage(id: number): Promise<void>
    {
        await this.deleteMessagesRemote([ id ]);
    }

    public async deleteMessages(ids: number[]): Promise<void>
    {
        await this.deleteMessagesRemote(ids);
    }

    public async clearAll(): Promise<void>
    {
        // delete/all only removes the current user's messages upstream, so we
        // resolve which loaded messages are ours and hand those to the engine to
        // drop locally + tombstone (other participants' messages stay).
        const email = this.currentUserEmail();
        const ownIds = email
            ? this.$messages.getValue().filter(m => this.sameEmail(m.user_email, email)).map(m => m.id)
            : [];
        await this.deleteAllRemote(ownIds);
    }

    public async loadUsers(): Promise<void>
    {
        await this.loadUsersPage();
    }

    public startPolling(): void
    {
        if (this.isPollingEnabled)
        {
            return;
        }
        this.resetPollDelay();
        this.pendingPollAfterCurrent = null;
        this.pollGeneration++;
        this.isPollingEnabled = true;
        this.schedulePoll();
    }

    public stopPolling(): void
    {
        this.clearConsentRetry();
        this.isPollingEnabled = false;
        this.pollGeneration++;
        if (this.officeService.getHost() instanceof OutlookHost)
        {
            this.hasInitialized = false;
            this.documentGeneration++;
            this.initialization = null;
        }
        if (this.pollTimer !== null)
        {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    public init(): Promise<void>
    {
        if (this.isDestroyed || !this.isAuthenticated)
        {
            return Promise.resolve();
        }
        if (this.officeService.getHost() instanceof OutlookHost && this.initialization)
        {
            return this.initialization;
        }
        const initialization = this.initInternal();
        this.initialization = initialization;
        void initialization.then(() =>
        {
            if (this.initialization === initialization)
            {
                this.initialization = null;
            }
        });
        return initialization;
    }

    private resetDocument(): void
    {
        this.documentGeneration++;
        this.document = null;
        this.documentId.next(null);
        this.isAvailable.next(!(this.officeService.getHost() instanceof OutlookHost));
        this.documentResolved = false;
        this.isIdentityUnavailable.next(false);
        this.needsMailConsent.next(false);
        this.clearConsentRetry();
        this.consentRequest = null;
        this.consentRequestInteractive = false;
        this.initialization = null;
        this.pollGeneration++;
        this.pendingPollAfterCurrent = null;
        this.resetPollDelay();
        if (this.pollTimer !== null)
        {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
        this.$messages.next([]);
        this.resetPagination();
        this.unreadCount.next(0);
        this.firstUnreadId.next(null);
    }

    private async initInternal(): Promise<void>
    {
        const host = this.officeService.getHost();
        const shouldResolve = !this.documentResolved || !(host instanceof OutlookHost) || !this.document;
        if (shouldResolve)
        {
            this.resetDocument();
        }
        const generation = this.documentGeneration;
        this.hasInitialized = true;
        this.isMessagesLoading.next(true);
        try
        {
            if (shouldResolve)
            {
                this.documentResolved = true;
                await this.resolveDocument();
                if (generation === this.documentGeneration && host instanceof OutlookHost)
                {
                    this.needsMailConsent.next(this.mailConsentRequired);
                    this.isIdentityUnavailable.next(!this.document);
                }
            }
            if (generation !== this.documentGeneration)
            {
                return;
            }
            this.documentId.next(this.document?.id ?? null);
            this.isAvailable.next(!(host instanceof OutlookHost) || !!this.document);
            const loadedMessages = await this.loadInitialMessages();
            if (generation === this.documentGeneration)
            {
                if (loadedMessages)
                {
                    this.isMessagesLoading.next(false);
                }
                this.syncReadState();
            }
        }
        catch (e)
        {
            if (generation === this.documentGeneration)
            {
                logger.error('groupChat: init failed', e);
            }
        }
        finally
        {
            if (generation === this.documentGeneration)
            {
                if (!this.document)
                {
                    this.isMessagesLoading.next(false);
                }
                this.schedulePoll();
                this.scheduleConsentRetry();
            }
        }
    }

    public onDestroy(): void
    {
        this.isDestroyed = true;
        this.stopPolling();
        this.resetDocument();
        this.isMessagesLoading.next(false);
        this.sessionSubscription.unsubscribe();
        const host = this.officeService.getHost();
        if (host instanceof OutlookHost)
        {
            host.offDocumentChange(this.documentChangeHandler);
        }
    }

    public getFirstUnreadMessageId(): number | null
    {
        // Computed on demand so the view scrolls to the true first unread
        // message at the moment it opens — before it gets marked read.
        return this.resolveFirstUnreadId();
    }

    public $firstUnreadId(): Observable<number | null>
    {
        return this.firstUnreadId.asObservable();
    }

    public $unreadCount(): Observable<number>
    {
        return this.unreadCount.asObservable();
    }

    public setWindowActive(isActive: boolean): void
    {
        if (this.isWindowActive === isActive)
        {
            return;
        }
        this.isWindowActive = isActive;
        this.syncReadState();
        this.clearConsentRetry();
        if (isActive && this.needsMailConsent.getValue())
        {
            void this.refreshMailConsent(false).catch(() => logger.error('groupChat: mail consent refresh failed'));
        }
    }

    public setViewActive(isActive: boolean): void
    {
        if (this.isViewActive === isActive)
        {
            return;
        }
        this.isViewActive = isActive;
        this.pollGeneration++;
        this.resetPollDelay();
        if (isActive)
        {
            if (this.isPolling)
            {
                this.pendingPollAfterCurrent = true;
            }
            else
            {
                this.schedulePoll(0, true);
            }
        }
        else
        {
            this.pendingPollAfterCurrent = null;
            this.schedulePoll();
        }
        this.syncReadState();
    }

    private resolvePollIntervals(): number[]
    {
        return this.isViewActive ? GROUP_POLL_INTERVALS_MS : AI_POLL_INTERVALS_MS;
    }

    private resolvePollInterval(): number
    {
        const intervals = this.resolvePollIntervals();
        return intervals[ Math.min(this.pollStep, intervals.length - 1) ];
    }

    private schedulePoll(delay?: number, isActivationRefresh = false): void
    {
        if (!this.isPollingEnabled || !this.isAuthenticated || this.isDestroyed
            || (!this.document && this.officeService.getHost() instanceof OutlookHost))
        {
            return;
        }
        if (this.pollTimer !== null)
        {
            clearTimeout(this.pollTimer);
        }
        const pollGeneration = this.pollGeneration;
        this.pollTimer = setTimeout(() =>
        {
            this.pollTimer = null;
            void this.safePoll(isActivationRefresh, pollGeneration);
        }, delay ?? this.resolvePollInterval());
    }

    private advancePollDelay(): void
    {
        this.pollAttempts++;
        if (this.pollAttempts < POLLS_PER_INTERVAL)
        {
            return;
        }
        this.pollAttempts = 0;
        this.pollStep = Math.min(this.pollStep + 1, this.resolvePollIntervals().length - 1);
    }

    private resetPollDelay(): void
    {
        this.pollStep = 0;
        this.pollAttempts = 0;
    }

    // Driven by the list's scroll position. Reaching the bottom while focused is
    // what finally marks the accumulated unread messages read.
    public setAtBottom(isAtBottom: boolean): void
    {
        if (this.isAtBottom === isAtBottom)
        {
            return;
        }
        this.isAtBottom = isAtBottom;
        this.syncReadState();
    }

    private canMarkRead(): boolean
    {
        return this.isWindowActive && this.isViewActive && this.isAtBottom;
    }

    // While the user is focused and at the bottom, keep the marker at the newest
    // message so the badge/separator stay cleared. Otherwise leave the marker in
    // place and just recompute the badge + separator anchor — except on the
    // first-ever encounter with a document, where we drop a baseline so its
    // pre-existing history isn't reported as unread.
    private syncReadState(): void
    {
        if (this.canMarkRead())
        {
            this.markRead();
            return;
        }
        if (this.readLastReadId() === null)
        {
            this.persistLastReadId();
        }
        this.refreshUnreadState();
    }

    private markRead(): void
    {
        this.persistLastReadId();
        this.refreshUnreadState();
    }

    private refreshUnreadState(): void
    {
        this.unreadCount.next(this.computeUnreadCount());
        this.firstUnreadId.next(this.resolveFirstUnreadId());
    }

    private computeUnreadCount(): number
    {
        const lastReadId = this.readLastReadId();
        if (lastReadId === null)
        {
            return 0;
        }
        const email = this.currentUserEmail();
        return this.$messages.getValue().filter(m =>
            m.id > lastReadId && !(email && this.sameEmail(m.user_email, email))).length;
    }

    private resolveFirstUnreadId(): number | null
    {
        const lastReadId = this.readLastReadId();
        if (lastReadId === null)
        {
            return null;
        }

        const email = this.currentUserEmail();
        const firstUnread = this.$messages.getValue().find(m =>
            m.id > lastReadId && !(email && this.sameEmail(m.user_email, email)));
        return firstUnread?.id ?? null;
    }

    private lastReadStorageKey(): string | null
    {
        return this.document ? `group_chat_last_read_${ this.document.id }` : null;
    }

    private readLastReadId(): number | null
    {
        const key = this.lastReadStorageKey();
        const raw = key ? localStorage.getItem(key) : null;
        const id = raw === null ? NaN : Number(raw);
        return Number.isFinite(id) ? id : null;
    }

    private persistLastReadId(): void
    {
        const key = this.lastReadStorageKey();
        const msgs = this.$messages.getValue();
        if (!key || this.isMessagesLoading.getValue())
        {
            return;
        }
        const lastReadId = msgs.length ? Math.max(...msgs.map(m => m.id)) : this.readLastReadId() ?? 0;
        localStorage.setItem(key, String(lastReadId));
    }

    private async safePoll(isActivationRefresh: boolean, pollGeneration: number): Promise<void>
    {
        if (pollGeneration !== this.pollGeneration)
        {
            if (!this.isPolling && this.pollTimer === null)
            {
                this.schedulePoll();
            }
            return;
        }
        if (this.isPolling)
        {
            this.pendingPollAfterCurrent = isActivationRefresh;
            return;
        }
        this.isPolling = true;
        const generation = this.documentGeneration;
        try
        {
            const newMessages = await this.pollNewMessages();
            if (generation !== this.documentGeneration)
            {
                return;
            }
            this.isMessagesLoading.next(false);
            this.syncReadState();
            if (pollGeneration === this.pollGeneration && !isActivationRefresh && newMessages > 0)
            {
                this.resetPollDelay();
            }
            else if (pollGeneration === this.pollGeneration && !isActivationRefresh)
            {
                this.advancePollDelay();
            }
        }
        catch (e)
        {
            logger.error('groupChat: poll failed', e);
            if (pollGeneration === this.pollGeneration && !isActivationRefresh)
            {
                this.advancePollDelay();
            }
        }
        finally
        {
            this.isPolling = false;
            const isNextPollActivationRefresh = this.pendingPollAfterCurrent;
            this.pendingPollAfterCurrent = null;
            if (isNextPollActivationRefresh !== null)
            {
                this.schedulePoll(0, isNextPollActivationRefresh);
            }
            else if (pollGeneration === this.pollGeneration || this.pollTimer === null)
            {
                this.schedulePoll();
            }
        }
    }

    private toUiMessages(msgs: IGroupChatMsg[], users: Map<string, IGroupChatUser>): IGroupChatUiMessage[]
    {
        const currentEmail = this.currentUserEmail();
        const byId = new Map(msgs.map(m => [ m.id, m ]));

        return msgs.map(msg =>
        {
            const parentId = msg.parent_id != null && msg.parent_id >= 0 ? msg.parent_id : null;
            const ui: IGroupChatUiMessage = {
                id: msg.id,
                parentId,
                text: msg.text,
                createdAt: msg.dt * 1_000,
                author: this.resolveAuthor(msg.user_email, users),
                isOwn: !!currentEmail && this.sameEmail(msg.user_email, currentEmail),
            };

            if (parentId !== null)
            {
                const parent = byId.get(parentId);
                if (parent)
                {
                    ui.replyTo = {
                        id: parent.id,
                        author: this.resolveAuthor(parent.user_email, users),
                        text: parent.text,
                    };
                }
            }

            return ui;
        });
    }

    private resolveAuthor(email: string, users: Map<string, IGroupChatUser>): IGroupChatAuthor
    {
        const user = users.get(email);
        const fullName = user ? `${ user.first_name ?? '' } ${ user.last_name ?? '' }`.trim() : '';
        const displayName = user?.display_name?.trim() || fullName || email;
        return { email, displayName };
    }

    private currentUserEmail(): string | null
    {
        try
        {
            return this.sessionService.getSession()?.user_email ?? null;
        }
        catch
        {
            return null;
        }
    }

    private sameEmail(a: string, b: string): boolean
    {
        return a.trim().toLowerCase() === b.trim().toLowerCase();
    }

    private defineHandlerForDocumentChange(): void
    {
        const host = this.officeService.getHost();
        host.onDocumentChange(this.documentChangeHandler);
    }

    private defineHandlerForSessionChange(): void
    {
        this.sessionSubscription.add(this.sessionService.sessionInfo$.subscribe(this.handlerForSessionChange.bind(this)));
        this.sessionSubscription.add(this.sessionService.authState$.subscribe(isAuthenticated =>
        {
            this.isAuthenticated = isAuthenticated === true;
            // An undecided session is not a completed history load. The shell
            // starts the first request after authentication becomes ready.
            if (isAuthenticated === false)
            {
                this.resetDocument();
                this.isMessagesLoading.next(false);
            }
            else if (isAuthenticated === true)
            {
                this.isMessagesLoading.next(true);
                if (this.hasInitialized)
                {
                    void this.init();
                }
            }
        }));
    }

    private handlerForSessionChange(info: ISessionInfo | null): void
    {
        if (!this.hasInitialized || this.isDestroyed)
        {
            return;
        }
        this.resetDocument();
        if (info?.user_id)
        {
            void this.init();
        }
        else
        {
            this.isMessagesLoading.next(false);
        }
    }
}
